import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useTitle } from "@/hooks/use-title";
import { useTranslation } from "@/i18n/LanguageContext";
import Header from "@/components/header";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Loader2, Plus, Github, ExternalLink } from "lucide-react";

// Define Code interface
interface Code {
  id: number;
  title: string;
  description: string;
  github_link: string;
  image_url?: string;
  userId: number;
  visible: number;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    displayName?: string;
    email: string;
    avatarUrl?: string;
  };
}

export default function CodesPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [_, setLocation] = useLocation();
  useTitle(t("header.codes") || "Codes");

  // Fetch all codes
  const { data: codes, isLoading, error } = useQuery<Code[]>({
    queryKey: ["/api/codes"],
  });

  // Function to handle "View on GitHub" button click
  const openGithubLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="container mx-auto pt-24 px-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>{t("error.title") || "Error"}</CardTitle>
              <CardDescription>
                {t("error.loading_codes") || "Error loading codes"}
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="container mx-auto pt-24 px-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white">
            {t("other.codes.title") || "Community Codes"}
          </h1>
          <Button onClick={() => setLocation("/codes/create")}>
            <Plus className="mr-2 h-4 w-4" />
            {t("other.codes.create") || "Add Code"}
          </Button>
        </div>

        {codes && codes.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>{t("other.codes.no_codes") || "No Codes Found"}</CardTitle>
              <CardDescription>
                {t("other.codes.be_first") || "Be the first to share your code!"}
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button onClick={() => setLocation("/codes/create")}>
                <Plus className="mr-2 h-4 w-4" />
                {t("other.codes.create") || "Add Code"}
              </Button>
            </CardFooter>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {codes?.map((code) => (
              <Card
                key={code.id}
                className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all duration-300 h-full flex flex-col overflow-hidden shadow-lg hover:shadow-xl hover:transform hover:scale-[1.02]"
              >
                {code.image_url ? (
                  <div className="aspect-video w-full overflow-hidden">
                    <img
                      src={code.image_url}
                      alt={code.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                  </div>
                ) : (
                  <div className="aspect-video w-full bg-gradient-to-br from-zinc-800 to-zinc-950 flex items-center justify-center">
                    <Github className="h-12 w-12 text-zinc-600 opacity-50" />
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-xl font-bold text-white">{code.title}</CardTitle>
                    <span className="text-xs px-2 py-1 rounded-full bg-zinc-800 text-zinc-400 font-medium">
                      {new Date(code.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <CardDescription className="flex items-center mt-1">
                    {code.user?.avatarUrl ? (
                      <img 
                        src={code.user.avatarUrl} 
                        alt={code.user?.displayName || ""} 
                        className="w-5 h-5 rounded-full mr-2"
                      />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-zinc-700 mr-2 flex items-center justify-center text-[10px] text-zinc-300">
                        {(code.user?.displayName || code.user?.email || "").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-zinc-400">
                      {code.user?.displayName || code.user?.email.split("@")[0]}
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow py-2">
                  <p className="text-sm text-zinc-400 line-clamp-3">{code.description}</p>
                </CardContent>
                <CardFooter className="pt-2 border-t border-zinc-800">
                  <div className="w-full flex justify-between items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-zinc-800 hover:bg-zinc-700 border-zinc-700 text-zinc-200"
                      onClick={() => openGithubLink(code.github_link)}
                    >
                      <Github className="mr-2 h-4 w-4" />
                      {t("other.codes.view_on_github") || "View on GitHub"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full hover:bg-zinc-800"
                      onClick={() => openGithubLink(code.github_link)}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
